import { useState } from 'react';
import { ChevronDown, ChevronRight, FileText } from 'lucide-react';
import { cn } from '../../lib/utils';
import type { WikiTreeNode } from '../../lib/wiki';

type Props = {
  nodes: WikiTreeNode[];
  selectedId: string | null;
  /** Ids that cannot be chosen (e.g. the page being moved and its subtree). */
  disabledIds?: ReadonlySet<string>;
  onSelect: (pageId: string) => void;
  /** Expand every branch that contains this id on first render. */
  expandPathTo?: ReadonlySet<string>;
  depth?: number;
};

/** Confluence-style page tree: every node is a page; branches toggle, leaves select. */
export default function WikiPageTree({
  nodes,
  selectedId,
  disabledIds,
  onSelect,
  expandPathTo,
  depth = 0,
}: Props) {
  return (
    <ul className={cn('space-y-px', depth > 0 && 'ml-3 border-l pl-1')} role={depth === 0 ? 'tree' : 'group'}>
      {nodes.map((node) => (
        <TreeRow
          key={node.page.id}
          node={node}
          selectedId={selectedId}
          disabledIds={disabledIds}
          onSelect={onSelect}
          expandPathTo={expandPathTo}
          depth={depth}
        />
      ))}
    </ul>
  );
}

function TreeRow({ node, selectedId, disabledIds, onSelect, expandPathTo, depth }: Omit<Props, 'nodes'> & { node: WikiTreeNode; depth: number }) {
  const hasChildren = node.children.length > 0;
  const [open, setOpen] = useState<boolean>(() => Boolean(expandPathTo?.has(node.page.id)) || depth === 0);
  const selected = node.page.id === selectedId;
  const disabled = Boolean(disabledIds?.has(node.page.id));

  return (
    <li role="treeitem" aria-expanded={hasChildren ? open : undefined} aria-selected={selected}>
      <div
        className={cn(
          'group flex items-center gap-1 rounded-md pr-2 text-sm',
          selected ? 'bg-primary/10 font-medium text-primary' : 'text-foreground hover:bg-accent/70',
          disabled && 'opacity-50',
        )}
      >
        {hasChildren ? (
          <button
            type="button"
            className="flex h-6 w-5 shrink-0 items-center justify-center text-muted-foreground"
            aria-label={open ? 'Collapse' : 'Expand'}
            onClick={() => setOpen((v) => !v)}
          >
            {open ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
          </button>
        ) : (
          <span className="flex h-6 w-5 shrink-0 items-center justify-center text-muted-foreground">
            <FileText className="h-3 w-3" aria-hidden />
          </span>
        )}
        <button
          type="button"
          className="min-w-0 flex-1 truncate py-1 text-left"
          title={node.page.title}
          disabled={disabled}
          onClick={() => onSelect(node.page.id)}
        >
          {node.page.title}
        </button>
      </div>
      {hasChildren && open ? (
        <WikiPageTree
          nodes={node.children}
          selectedId={selectedId}
          disabledIds={disabledIds}
          onSelect={onSelect}
          expandPathTo={expandPathTo}
          depth={depth + 1}
        />
      ) : null}
    </li>
  );
}
